import { isSameMinute, isToday, subHours } from 'date-fns'

import type { AkiParamsItem, VitalSignItemDTO } from '@/generated/api'
import { VitalSignsKeys } from '../../models/VitalSignsKeys'
import { keysWithTwoDecimalPlaces } from '@/util/formatLaboratoryData'
import { DateFormat } from '@/enums/DateFormat'
import i18n from '@/i18n/i18n'

export function findLatestValueByKey(vitalSigns: VitalSignItemDTO[], key: VitalSignsKeys) {
  const latestItem = getLatestVitalSignItem(vitalSigns.filter((el) => el.typeKey === key) || [])

  let value = latestItem?.numericValue.toFixed(0) ?? '–'

  if (key === VitalSignsKeys.TEMP) {
    value = latestItem?.numericValue.toFixed(1) ?? '–'
  }
  if (keysWithTwoDecimalPlaces.includes(key)) {
    value = latestItem?.numericValue.toFixed(2) ?? '–'
  }

  return {
    value,
    hasMeasurement: latestItem !== undefined,
    measurementDate: latestItem?.measurementDate ?? '–',
    unit: latestItem?.unit
  }
}

export function findLatestAkiValueByKey(vitalSigns: AkiParamsItem[], key: VitalSignsKeys) {
  const latestItem = getLatestAkiParamsItem(vitalSigns.filter((el) => el.typeKey === key) || [])

  let value = latestItem?.observationValue.toFixed(0) ?? '–'

  if (key === VitalSignsKeys.TEMP) {
    value = latestItem?.observationValue.toFixed(1) ?? '–'
  }
  if (keysWithTwoDecimalPlaces.includes(key)) {
    value = latestItem?.observationValue.toFixed(2) ?? '–'
  }

  return {
    value,
    hasMeasurement: latestItem !== undefined,
    measurementDate: latestItem?.observationTime ?? '–',
    unit: latestItem?.unit
  }
}

function getLatestVitalSignItem(vitalSigns: VitalSignItemDTO[]): VitalSignItemDTO | undefined {
  if (vitalSigns.length === 0) {
    return undefined
  }

  return vitalSigns.reduce((latest, current) => {
    return new Date(current.measurementDate) > new Date(latest.measurementDate) ? current : latest
  })
}

export function sumLast24hAkiValueByKey(paramsItems: AkiParamsItem[], key: VitalSignsKeys) {
  const cutoff = subHours(new Date(), 24)
  const itemsInWindow = paramsItems
    .filter((el) => el.typeKey === key)
    .filter((el) => new Date(el.observationTime) >= cutoff)

  if (itemsInWindow.length === 0) {
    return {
      value: '–',
      hasMeasurement: false,
      measurementDate: '–'
    }
  }

  const total = itemsInWindow.reduce((sum, item) => sum + item.observationValue, 0)
  const latestItem = getLatestAkiParamsItem(itemsInWindow)

  return {
    value: total.toFixed(0),
    hasMeasurement: true,
    measurementDate: latestItem?.observationTime ?? '–'
  }
}

function getLatestAkiParamsItem(paramsItems: AkiParamsItem[]): AkiParamsItem | undefined {
  if (paramsItems.length === 0) {
    return undefined
  }

  return paramsItems.reduce((latest, current) => {
    return new Date(current.observationTime) > new Date(latest.observationTime) ? current : latest
  })
}

export function findLatestTimeValueByKey(
  vitalSigns: VitalSignItemDTO[],
  key: VitalSignsKeys
): string {
  const value = getLatestVitalSignItem(
    vitalSigns.filter((el) => el.typeKey === key) || []
  )?.measurementDate

  if (value === undefined) {
    return '–'
  }

  return formatTimestamp(value)
}

export function findLatestAkiTimeValueByKey(
  paramsItems: AkiParamsItem[],
  key: VitalSignsKeys
): string {
  const value = getLatestAkiParamsItem(
    paramsItems.filter((el) => el.typeKey === key) || []
  )?.observationTime

  if (value === undefined) {
    return '–'
  }

  return formatTimestamp(value)
}

export function formatTimestamp(timestamp: string) {
  const date = new Date(timestamp)
  const now = new Date()

  if (isSameMinute(date, now)) {
    return 'Now'
  }

  if (isToday(date)) {
    return i18n.global.d(date, DateFormat.TIME)
  }

  return i18n.global.d(date, DateFormat.DATE_TIME)
}
